/**
 * Shared level-loader helpers for all four web prototypes.
 *
 * The level shape is per-proto (each proto's `levels.ts` defines its own
 * exported array). This module just provides identity + cyclic navigation,
 * so per-proto code can read uniformly:
 *
 *   import { levels } from "./levels";
 *   import { loadLevels, getLevel, nextLevelIndex } from "@core/level-loader";
 *   const all = loadLevels(levels);
 *   const cur = getLevel(all, idx);
 *   idx = nextLevelIndex(all, idx); // loops back to 0 after the last
 *
 * Kept tiny on purpose — it's a seam for future async loading (JSON fetch,
 * remote config) without touching every proto's main.ts.
 */
export function loadLevels<T>(jsonOrModule: T[]): T[] {
  // Pass-through today; future versions may validate / normalize.
  return jsonOrModule.slice();
}

/**
 * Safe accessor — clamps the index to a valid range. Useful when a saved
 * progress value drifts out of range after a content update.
 */
export function getLevel<T>(levels: T[], index: number): T {
  if (levels.length === 0) {
    throw new Error("loadLevels: empty level list");
  }
  const n = levels.length;
  const wrapped = ((index % n) + n) % n;
  return levels[wrapped];
}

/**
 * Next level index, wrapping back to 0 after the last level.
 * Per Voodoo "unloseable first 10" spec we don't end the game — looping the
 * 5 hand-tuned levels is fine for a V0 prototype.
 */
export function nextLevelIndex<T>(levels: T[], current: number): number {
  if (levels.length === 0) return 0;
  return (current + 1) % levels.length;
}

/**
 * Human-friendly level number (1-based) for use in the HUD.
 */
export function levelNumber(index: number): number {
  return index + 1;
}
